import { DatePipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import { AssetBasketService, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  CustomRouteAgencyMap,
  DateNotExpiredPipe,
  ENVIRONMENT,
  FilterAllOptions,
  FilterFormDataModel,
  SearchAssetsFieldPlaceholder,
  SpDateFormatPipe,
  StepperModel,
  SystemPreferencesService,
  ValueListsService,
  ViewType,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, Collection, CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { Subject, Subscription, catchError, lastValueFrom, map, of, switchMap, tap, throwError } from 'rxjs';
import { customerAssetFilters } from '../../../customer/customer-asset-filters';
import { AssetCommonService } from '../../../services/asset-common.service';
import { AssetFiltersService } from '../../../services/asset-filters.service';
import { AssetViewTypeService, GridConfig } from '../../../services/asset-view-type.service';
import { CollectionPublishingStepperService } from '../../../services/collection-publishing-stepper.service';
import { SharingStepperDialogService } from '../../../services/sharing-stepper-dialog.service';
import { AssetsPageService } from '../../assets/assets-page/assets-page.service';
import { FilterBubblesUtil } from '../../assets/assets-page/utils/filter-bubbles.util';
import { FilterFormData } from '../../assets/filter-form-data';
import { CollectionInfo } from '../collection-info.model';
import { SelectCollectionPopupComponent } from '../components/select-collection-popup/select-collection-popup.component';
import { CollectionAssetPreviewService } from '../services/collection-asset-preview.service';
import { CollectionEventsService } from '../services/collection-events.service';
import { CollectionSelectionService } from '../services/collection-selection.service';
import { ThumbnailListComponent } from './thumbnail-list/thumbnail-list.component';

@Component({
  selector: 'thehubz-collection-asset-view',
  templateUrl: './collection-asset-view.component.html',
  styleUrls: ['./collection-asset-view.component.scss'],
})
export class CollectionAssetListViewComponent implements OnInit, OnDestroy {
  @ViewChild(RouterOutlet) outlet!: RouterOutlet;
  @Input() gridSettingsKeySuffix = 'CollectionOverview';
  /* Use formResetter when filter bubble is cleared */
  formResetter = new Subject<{ _id: string; name: string; key: string } | undefined>();
  collectionData: CollectionInfo[] = [];
  collectionId!: string;
  loading = false;
  assetsDetailList: AssetUIModel[] = [];
  stepperData!: StepperModel;
  filteredList: AssetUIModel[] = [];
  skipFilterProperty: string[] = [];
  assetCount = 0;
  navLinks: {
    path: string;
    label: string;
  }[] = [];
  currentTab = '';
  appliedFilters = 0;
  filterDetails: AnyType = {};
  collectionInfo!: CollectionInfo;
  showEmptyState = false;
  offset = 0;
  size = 4;
  currentSize?: number;
  sizes = AppSettings.SCREEN_SIZES;
  itemSize = 310;
  selectedViewType!: ViewType;
  selectedViewSection2: ViewType = 'sortByLastCreated';
  sortByNew = true;
  private readonly filterBubblesUtil!: FilterBubblesUtil;

  readonly brandingKey: CustomClients;

  collectionPublished = false;
  collectionOwner = false;
  searchText = '';
  searchPlaceholder = SearchAssetsFieldPlaceholder;
  isMobileSearchOpen = false;

  removeAssetSub!: Subscription;
  collectionSub!: Subscription;

  AnyAsset = anyAsset;
  filterBubbles: { _id: string; name: string }[] = [];

  defaultSort: Record<string, number> = { createdAt: -1 };
  sortData: Record<string, number> = {};

  gridConfig: GridConfig = {
    showListView: false,
    showGridView: true,
    showGridViewSmall: true,
    showGridViewLarge: false,
    showGridViewSidebar: false,
    gridSize: 4,
  };

  readonly defaultLimit = 48;

  filterObject: FilterAllOptions = {
    filters: {},
    sort: {},
    limit: 48,
    skip: 0,
  };

  selectedFilterObject: AnyType;
  filterCollectionFormData!: FilterFormDataModel;
  historyCount?: number;

  dialogSelectRef!: MatDialogRef<SelectCollectionPopupComponent>;
  dialogSelectState?: number;

  initialFlag = true;
  filterAppliedFlag = false;

  routedChildComponent!: AnyType; // Will hold reference to routed child component
  collectionAssetsRecord!: Array<AssetUIModel>;

  private exportAssetInfoSub?: Subscription;
  private removeFilterBubblesSubs?: Subscription;
  private removeAllFilterBubblesSubs?: Subscription;
  private filterActionsSubs?: Subscription;
  private formDataSubs?: Subscription;
  private reloadCounterSubs?: Subscription;
  private updateCollectionSub?: Subscription;

  constructor(
    @Inject(ENVIRONMENT) protected readonly env: ClientEnvironment,
    datePipe: DatePipe,
    spDatePipe: SpDateFormatPipe,
    private router: Router,
    private canPipe: CanPipe,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private assetService: AssetsApiService,
    private confirmDialog: ConfirmDialogService,
    private commonUtilityLib: CommonUtilityLib,
    private valueListsService: ValueListsService,
    private collectionAPI: CollectionsApiService,
    private assetsPageService: AssetsPageService,
    private assetCommonService: AssetCommonService,
    private dateNotExpiredPipe: DateNotExpiredPipe,
    private assetBasketService: AssetBasketService,
    private assetFiltersService: AssetFiltersService,
    private assetUrlService: AssetsGenerateUrlService,
    private assetViewTypeService: AssetViewTypeService,
    private collectionAPIService: CollectionsApiService,
    private downloadAssetsService: DownloadAssetsService,
    private collectionEventsService: CollectionEventsService,
    private systemPreferencesService: SystemPreferencesService,
    private collectionSelectionService: CollectionSelectionService,
    private sharingStepperDialogService: SharingStepperDialogService,
    private clientNotificationService: ClientNotificationHandlerService,
    private collectionAssetPreviewService: CollectionAssetPreviewService,
    private collectionPublishingStepperService: CollectionPublishingStepperService,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.collectionId = params['id'];
      if (!this.collectionId) {
        throw new Error('Collection ID is missing');
      }
    });
    this.brandingKey = this.env.brandingKey;
    this.filterBubblesUtil = new FilterBubblesUtil(datePipe, spDatePipe);
    this.initAssetView();
  }

  ngOnInit() {
    this.buildFilterForm();
    this.skipFilterProperty = [AssetMetadata.General.TAGS, 'searchText'];
    const urlSplitArr: string[] = this.router.url.split('/');
    this.currentTab = urlSplitArr[urlSplitArr.length - 1];

    if (this.collectionId) {
      this.commonUtilityLib.emitNewHeaderline(null);
      this.collectionAPIService
        .collectionsControllerFindOne(this.collectionId)
        .pipe(
          tap((resp: Collection) => {
            this.commonUtilityLib.emitNewHeaderline(resp.name);
          }),
          switchMap((resp: Collection) => {
            // Handle the first observable response (collectionInfo)
            this.collectionInfo = resp;
            if (this.collectionInfo.published && this.collectionInfo.published.length > 0) {
              this.collectionPublished = true;
            }
            this.collectionAssetsRecord = JSON.parse(JSON.stringify(this.collectionInfo.assets));
            this.collectionOwner = this.assetCommonService.checkCollectionOwner(this.collectionInfo);

            this.getNavLinks();
            this.collectionEventsService.collectionDataSubject$.next(resp);

            return of(sessionStorage.getItem(AppSettings.COLLECTION_FILTER_STRING) as string);
          }),
        )
        .subscribe((existingFilterObject) => {
          const getFilterData = JSON.parse(existingFilterObject);
          if (getFilterData) {
            const selectedCollectionFilter = getFilterData[this.collectionId];
            if (selectedCollectionFilter) {
              this.appliedFilters = this.commonUtilityLib.filterDataCount(selectedCollectionFilter.selectedFilterData, this.skipFilterProperty);
              this.selectedFilterObject = selectedCollectionFilter.selectedFilterData;
              this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterCollectionFormData, this.selectedFilterObject);
              this.searchText = selectedCollectionFilter.searchedText;
              this.applyFilters(this.selectedFilterObject);
              this.initialFlag = false;
            } else {
              this.applyFilters();
            }
          } else {
            this.applyFilters();
          }

          this.subscribeToFilterActions();
        });
    }

    this.removeAssetSub = this.assetCommonService.collectionRemoveAsset$.subscribe((data) => {
      if (data && data[0].assetID?.length) this.removeFromCollection(data);
    });
    this.selectedViewType = this.assetViewTypeService.getViewType(this.gridSettingsKeySuffix);

    this.exportAssetInfoSub = this.assetCommonService.exportAssetInfo.subscribe((data) => {
      if (data) this.assetsPageService.exportAssets(this.showEmptyState);
    });
    this.removeAllFilterBubblesSubs = this.collectionEventsService.removeAllCollectionFilterBubble.subscribe((data) => {
      if (data) this.removeAllFilterBubble();
    });
    this.removeFilterBubblesSubs = this.collectionEventsService.removeCollectionFilterBubble.subscribe((data) => {
      if (data) this.removeFilterBubble(data);
    });
    this.formDataSubs = this.collectionEventsService.collectionUpdateFormDataSubject$.subscribe((data) => {
      if (data) this.updateFormData(data);
    });

    this.reloadCounterSubs = this.collectionEventsService.collectionsReloadCounterSubject$.subscribe((scroll) => {
      let scrollType = '';
      if (scroll.offset < this.offset) {
        scrollType = 'top';
      }
      this.offset = scroll.offset;
      this.assetFindAll(true).subscribe({
        next: (response: AnyType) => {
          let newFilteredList: AssetUIModel[] = [];
          if (scrollType === 'top') {
            newFilteredList = [...response.result, ...this.filteredList] as AssetUIModel[];
          } else {
            newFilteredList = [...this.filteredList, ...response.result] as AssetUIModel[];
          }
          const columns = this.size;

          const addedAssets = this.filterObject.limit || 0;
          const addedRows = Math.ceil(addedAssets / columns);

          setTimeout(() => {
            if (scroll.direction === 'top' && this.assetCount > 0 && scroll.anchorIndex != null) {
              if (this.routedChildComponent && typeof this.routedChildComponent.scrollGalleryToIndex === 'function') {
                this.routedChildComponent.scrollGalleryToIndex(scroll.anchorIndex + addedRows, 'auto');
              }
            }
          });
          this.filteredList = newFilteredList;
          this.collectionEventsService.collectionAssetDataSubject$.next(this.filteredList);
          this.collectionEventsService.collectionAssetCountSubject$.next(response.count);
          this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
          this.collectionEventsService.collectionFilterBubblesSubject$.next(this.filterBubbles);
        },
        error: (err: AnyType) => {
          this.clientNotificationService.errorNotification(err);
        },
      });
    });
  }

  onRouteActivate(componentRef: AnyType) {
    // when the routed child is ThumbnailListComponent, keep reference
    if (componentRef && componentRef instanceof ThumbnailListComponent) {
      this.routedChildComponent = componentRef;
    } else {
      this.routedChildComponent = null;
    }
  }

  subscribeToFilterActions() {
    this.filterActionsSubs = this.collectionEventsService.collectionApplyFilterSubject$.subscribe((data) => {
      this.initialFlag = Object.keys(data).length !== 0;
      if (this.initialFlag) {
        const collectionFilterData = data[this.collectionId];
        if (collectionFilterData === undefined || Object.keys(collectionFilterData).length) {
          this.filterAppliedFlag = collectionFilterData === undefined ? false : true;
          this.applyFilters(collectionFilterData);
        }
        this.initialFlag = false;
      }
    });
  }

  /**
   * get navigation links
   * includes custom client logic
   */
  async getNavLinks() {
    const agencyName = await this.assetCommonService.getCurrentUserAgencyName();
    // get the custom clientRoutes based on the user agency
    const clientRoutes = CustomRouteAgencyMap.filter((routeAg) => routeAg.agencies.includes(agencyName.toLowerCase() as CustomClients)).map(
      (routeAg) => routeAg.route,
    );

    // build the navigation links if custom client then add the extra link or show the standard onces
    this.navLinks =
      this.activatedRoute.routeConfig && this.activatedRoute.routeConfig.children
        ? this.commonUtilityLib.buildNavItems(this.activatedRoute.routeConfig.children, clientRoutes, undefined, this.collectionOwner, this.brandingKey)
        : [];
    this._handleRoleSpecificRoutes();
  }

  /**
   * Open stepper dialog for collection publishing
   */
  async publishCollection() {
    // here updating the collectionInfo once get updated with publishing data
    if ([CustomClients.Arval, CustomClients.VKB, CustomClients.SEAT].includes(this.brandingKey)) {
      const listUserAgencies = await lastValueFrom(this.valueListsService.listUserAgencies());
      this.confirmDialog
        .open({
          title: $localize`Confirmation`,
          message: $localize`Are you sure you want to publish this collection with all users of the system?`,
        })
        .subscribe((confirm) => {
          if (confirm) {
            const published = this.collectionSelectionService.getPublishCollectionDto(listUserAgencies);
            this.collectionAPI.collectionsControllerPublish(this.collectionInfo._id, { published }).subscribe({
              next: (response) => {
                if (response) {
                  this.clientNotificationService.successNotification($localize`Collection "${this.collectionInfo.name}" has been published `);
                  this.collectionInfo = response;
                }
              },
              error: (err) => {
                this.clientNotificationService.errorNotification(err);
              },
            });
          }
        });
    } else {
      this.collectionSub = (await this.collectionPublishingStepperService.collectionPublishingStepper(this.collectionInfo)).subscribe(
        (data) => (this.collectionInfo = data),
      );
    }
  }

  unpublishCollection(id: string) {
    this.confirmDialog
      .open({
        title: $localize`Unpublish collection`,
        message: $localize`Are you sure you want to unpublish this collection?`,
      })
      .subscribe((confirm) => {
        if (confirm && id) {
          this.collectionAPIService.collectionsControllerUpdate(id, { published: [] }).subscribe({
            next: async (result) => {
              if (result) {
                this.collectionInfo.published = result.published;
                this.collectionPublished = false;
                this.clientNotificationService.successNotification($localize`Collection was unpublished`);
                this.router.navigate(['/collections/published']);
              }
            },
            error: (err) => {
              this.clientNotificationService.errorNotification(err);
            },
          });
        }
      });
  }

  fetchCollections(collections: string[]) {
    if (collections.length > 0) {
      const collectionListData: AnyType = { list: collections };
      this.collectionAPIService.collectionsControllerFindCollectionList(JSON.stringify(collectionListData)).subscribe({
        next: async (response) => {
          this.collectionData = response.result;
          if (this.collectionData.length) {
            this.collectionData = await this.collectionAssetPreviewService.generateCoverPreview(this.collectionData);
            this.initiatePopUp();
          }
        },
      });
    } else {
      this.initiatePopUp();
    }
  }

  async initiatePopUp() {
    const collectionInfoData = JSON.parse(JSON.stringify(this.collectionInfo));
    const idx = collectionInfoData.assets.findIndex((asset: Asset) => asset._id === collectionInfoData.coverAssetID._id);
    collectionInfoData.coverAssetID = collectionInfoData.assets[idx];
    let collectionData = [collectionInfoData];
    collectionData = await this.collectionAssetPreviewService.generateCoverPreview(collectionData);
    this.dialogSelectRef = this.dialog.open(SelectCollectionPopupComponent, {
      maxWidth: '120rem',
      width: '60%',
      height: '80%',
      data: {
        callFrom: 'collection-details-view',
        selectedCollection: this.collectionData,
        collectionList: collectionData,
      },
    });
    this.dialogSelectState = this.dialogSelectRef.getState();
    this.dialogSelectRef.disableClose = true;
    this.dialogSelectRef.afterClosed().subscribe(() => {
      this.dialogSelectState = undefined;
    });
  }

  async addToDashboard() {
    const data = await this.systemPreferencesService.getUserPreferences();
    const collectionData = data.dashboardCollection || [];
    this.fetchCollections(collectionData);
  }

  /**
   * shareCollection method calls sharingStepperDialogService service to
   * create stepper model for collection sharing
   */
  shareCollection() {
    const stepLabel = $localize`Share collection:` + ` <span class="font-light">${this.collectionInfo.name}<span>`;
    const assetIDs = this.collectionInfo.assets?.map((asset) => asset._id ?? asset.toString()) ?? [];
    this.assetService.assetsControllerFindMany({ ids: assetIDs, optFor: 'share' }).subscribe({
      next: (response) => {
        const assetAspectRatio = response.result.map((asset) => ({
          assetId: asset._id,
          name: asset.name,
          version: asset.currentVersion?.version || 0,
          aspectRatios: asset.currentVersion?.aspectRatios || [],
        }));
        this.sharingStepperDialogService.collectionSharingStepper(assetAspectRatio, this.collectionInfo, this.collectionId, stepLabel);
      },
      error: (err: AnyType) => {
        this.clientNotificationService.errorNotification(err);
      },
    });
  }

  /**
   * Delete the collection
   * @param id
   */
  deleteCollection() {
    this.confirmDialog
      .open({
        title: $localize`Delete confirmation`,
        message: $localize`Are you sure you want to delete this collection?`,
      })
      .subscribe((confirm) => {
        if (confirm) {
          this.collectionAPIService.collectionsControllerRemove(this.collectionId.toString()).subscribe(() => {
            this.collectionSelectionService.updateSelectedItems('collection', this.assetsDetailList, false);
            this.router.navigate(['/collections']);
          });
        }
      });
  }

  assetsToBeRemoved(assets: string[]) {
    assets.forEach((assetId: string) => {
      if (this.collectionInfo?.assets && this.collectionInfo?.assets.length) {
        const idx = this.collectionInfo.assets.findIndex((x) => x._id == assetId);
        this.collectionInfo.assets.splice(idx, 1);
      }
    });
  }

  updateAssetDetailList(assets: string[]) {
    assets.forEach((assetId: string) => {
      if (this.assetsDetailList && this.assetsDetailList.length) {
        const idx = this.assetsDetailList.findIndex((x) => x._id == assetId);
        this.assetsDetailList.splice(idx, 1);
      }
    });
  }

  removeFromCollection(event: { assetID?: string; assetIdx?: string }[]) {
    const assetIds: string[] = [];
    event.forEach((element: AnyType) => assetIds.push(element.assetID));
    if (this.collectionInfo?.assets && this.collectionInfo?.assets.length <= assetIds.length) {
      this.clientNotificationService.errorStringNotification($localize`At least one asset is required for collection`);
      return;
    }
    if (assetIds.length) this.assetsToBeRemoved(assetIds);
    if (this.collectionInfo.assets?.length) {
      const assets = this.collectionInfo.assets?.map((asset) => asset._id);
      this.collectionAPIService.collectionsControllerUpdate(this.collectionId, { assets: assets }).subscribe(() => {
        this.updateAssetDetailList(assetIds);
        const currentUrl = this.router.url.split('?')[0];
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentUrl]);
        });
        let msg = assetIds.length > 1 ? $localize`Assets have been removed from collection` : $localize`Asset has been removed from collection`;
        msg += ` "${this.collectionInfo.name}"`;
        this.clientNotificationService.successNotification(msg);
      });
    }
  }

  sortCollection(event: AnyType) {
    this.sortByNew = !this.sortByNew;
    this.selectedViewSection2 = event || 'sortByLastCreated';
    if (!this.sortByNew) {
      this.sortData = { name: 1 };
    } else {
      this.sortData = this.defaultSort;
    }
    this.filterObject['sort'] = this.sortData;
    this.assetFindAll().subscribe({
      next: (response: AnyType) => {
        this.filteredList = response.result;
        this.collectionEventsService.collectionAssetDataSubject$.next(this.filteredList);
        this.collectionEventsService.collectionAssetCountSubject$.next(response.count);
        this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
      },
      error: (err: AnyType) => {
        this.clientNotificationService.errorNotification(err);
      },
    });
  }

  applyFilters(filterData?: AnyType) {
    const sortData = Object.keys(this.sortData).length === 0 ? this.defaultSort : this.sortData;

    const assetFutureLimit = sessionStorage.getItem('assetFutureLimit');
    let loadMoreFlag = false;
    if (assetFutureLimit && assetFutureLimit != null) {
      this.filterObject.skip = parseInt(assetFutureLimit) - 48;
      sessionStorage.removeItem('assetFutureLimit');
      this.offset = parseInt(assetFutureLimit) / 48 - 1;
      this.collectionEventsService.collectionsReloadOffsetSubject$.next(this.offset);
      loadMoreFlag = true;
    } else {
      loadMoreFlag = false;
      this.collectionEventsService.collectionsReloadOffsetSubject$.next(0);
    }

    if (filterData && Object.keys(filterData).length) {
      this.setFilterData(filterData, sortData, true);
      // Clean filtered data and store it in a separate query string
      const selectedFilterData: AnyType = filterData ? Object.fromEntries(Object.entries(filterData).filter(([_, v]) => v && v != '')) : {};
      for (const key in selectedFilterData) {
        if (selectedFilterData[key]._id || selectedFilterData[key].id) {
          selectedFilterData[key] = {
            _id: selectedFilterData[key]._id || selectedFilterData[key].id,
            name: selectedFilterData[key].name,
          };
        }
      }
      this.selectedFilterObject = selectedFilterData;
      this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterCollectionFormData, this.selectedFilterObject);
      this.assetCommonService.selectedFilterDataSubject$.next({ selectedFilterObject: this.selectedFilterObject, filterObject: this.filterObject });
    } else {
      this.appliedFilters = 0;
      delete this.filterObject['filters']['$or'];
      this.setFilterData(filterData, sortData, true);
      this.assetCommonService.selectedFilterDataSubject$.next({ selectedFilterObject: this.selectedFilterObject, filterObject: this.filterObject });
    }
    this.assetFindAll(loadMoreFlag).subscribe({
      next: (response: AnyType) => {
        this.filteredList = response.result;
        this.collectionEventsService.collectionAssetDataSubject$.next(this.filteredList);
        this.collectionEventsService.collectionAssetCountSubject$.next(response.count);
        this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
        this.collectionEventsService.collectionFilterBubblesSubject$.next(this.filterBubbles);
      },
      error: (err: AnyType) => {
        this.clientNotificationService.errorNotification(err);
      },
    });
  }

  private assetFindAll(loadMore = false): AnyType {
    this.resetOnLoadMoreFlag(loadMore);

    this.searchText = this.searchText?.trim();

    return this.assetService.assetsControllerFindAll(this.searchText, JSON.stringify(this.filterObject)).pipe(
      map((response) => {
        const basketIDs = this.assetBasketService.assetIdsFromMyBasket;
        this.assetCount = response.count;
        this.showEmptyState = response.count === 0;
        const previewAsset = this.assetUrlService.generatePreview(response.result);
        this.assetsDetailList = [...previewAsset];
        const isFilterObjectEmpty = Object.keys(this.filterObject['selectedFilterData'] || {}).length === 0;
        if (!isFilterObjectEmpty) this.collectionInfo.assets = this.assetsDetailList;
        else this.collectionInfo.assets = this.collectionAssetsRecord;

        this.assetsDetailList.forEach((asset) => {
          asset.isAddedToBasket = basketIDs.includes(asset._id);
        });

        return Object.assign(response, { result: [...this.assetsDetailList] });
      }),
      catchError((err: AnyType) => {
        return throwError(() => err);
      }),
    );
  }

  private setFilterData(filterData?: AnyType, sortData?: AnyType, sortFlag?: boolean) {
    this.assetFiltersService.setFilterData(
      { filterData, sortData, sortFlag, assetFilterString: AppSettings.COLLECTION_FILTER_STRING, collectionId: this.collectionId },
      this.filterObject,
      this.searchText,
    );
    this.selectedFilterObject = this.filterObject['selectedFilterData'];
    this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterCollectionFormData, this.selectedFilterObject);
    this.appliedFilters = this.commonUtilityLib.filterDataCount(this.selectedFilterObject, this.skipFilterProperty);
  }

  private resetOnLoadMoreFlag(loadMoreFlag?: boolean) {
    if (!loadMoreFlag) {
      this.filterObject['limit'] = this.filterAppliedFlag ? this.assetCount : this.defaultLimit;
      this.filterObject['skip'] = 0;

      // Trigger reset of reload on scroll
      this.collectionEventsService.collectionsReloadOffsetSubject$.next(0);
    }
  }

  addOnEnter() {
    if (this.searchText) {
      const searchBubble = { _id: this.searchText, name: this.searchText };
      this.applyFilters({ ...this.selectedFilterObject, searchText: searchBubble });
      this.searchText = '';
    } else {
      this.applyFilters({ ...this.selectedFilterObject });
    }
  }

  /**
   * Remove single filer bubble
   * @param filterBubble
   */
  removeFilterBubble(filterBubble: { _id: string; name: string; key: string }) {
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.loading = true;
    }
    this.formResetter.next(filterBubble);

    if (this.selectedFilterObject) {
      this.filterBubbles = this.filterBubblesUtil.removeFilterBubble(filterBubble, this.selectedFilterObject, this.filterObject) ?? [];
      this.applyFilters(this.selectedFilterObject);
    }
    if (this.brandingKey === CustomClients.Arval) this.setVisibleCustomFields();
  }
  /**
   * Clear all filter bubbles
   */
  removeAllFilterBubble() {
    this.resetForm();
    this.filterBubblesUtil.removeAllFilterBubble();
    this.searchText = '';
    this.formResetter.next(undefined);
    this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
    this.applyFilters();
    if (this.brandingKey === CustomClients.Arval) this.setVisibleCustomFields();
  }

  restFormField() {
    this.collectionEventsService.collectionUpdateSidebar$.next({ formControlName: 'all' });
    if (this.brandingKey === CustomClients.Arval) this.setVisibleCustomFields();
  }

  /**
   * Set visible property of channel and type field
   */
  private setVisibleCustomFields() {
    this.filterCollectionFormData.formFields.forEach((item) => {
      switch (item.formFieldName) {
        case 'channel': {
          const productIndex = this.commonUtilityLib.findArrayIndex(this.filterCollectionFormData, 'product');
          item.visible = !!this.selectedFilterObject?.[this.filterCollectionFormData.formFields[productIndex].formFieldName || ''];
          break;
        }
        case 'type': {
          const channelIndex = this.commonUtilityLib.findArrayIndex(this.filterCollectionFormData, 'channel');
          item.visible = !!this.selectedFilterObject?.[this.filterCollectionFormData.formFields[channelIndex].formFieldName || ''];
          break;
        }
      }
    });
  }

  currentTabClick(path: string) {
    this.currentTab = path;
  }

  onDownloadClick() {
    const assetIds = this.filteredList.filter((asset) => this.dateNotExpiredPipe.transform(asset.expiryDate) === true).map((asset) => asset._id);
    this.downloadAssetsService.downloadAssets(assetIds, `${this.collectionInfo.name}.zip`, this.collectionInfo.name);
  }

  initAssetView() {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    if (Object.keys(gridSettings).length) {
      this.gridConfig = gridSettings;
      return;
    }
    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
  }

  getItemsSelectedData(dataType: string) {
    const itemsSelected = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
    return itemsSelected[1]?.[dataType];
  }

  /**
   * SearchField methods
   */

  searchTextChanged(event: AnyType) {
    this.searchText = event;
    this.assetFindAll().subscribe({
      next: (response: AnyType) => {
        this.filteredList = response.result;
        this.assetCommonService.selectedFilterDataSubject$.next({ selectedFilterObject: this.selectedFilterObject, filterObject: this.filterObject });
        this.collectionEventsService.collectionAssetDataSubject$.next(this.filteredList);
        this.collectionEventsService.collectionAssetCountSubject$.next(response.count);
        this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
      },
      error: (err: AnyType) => {
        this.clientNotificationService.errorNotification(err);
      },
    });
  }

  clearSearch(event: AnyType) {
    this.searchText = event;
    this.resetForm();
  }

  buildFilterForm() {
    this.filterCollectionFormData = customerAssetFilters(this.env.brandingKey) ?? FilterFormData.FORM_DATA;
    const statusField = this.filterCollectionFormData.formFields?.find((field) => field.formFieldName === 'status');
    if (statusField?.options.length === 0) {
      if (this.brandingKey == CustomClients.LufthansaMediacenter || this.brandingKey == CustomClients.LufthansaMedialounge) {
        statusField.options = this.assetCommonService.getLufthansaAssetFilterStatusList(this.canPipe, this.AnyAsset);
      } else if (this.brandingKey == CustomClients.VKB) {
        statusField.options = this.assetCommonService.getVKBAssetFilterStatusList(this.canPipe, this.AnyAsset);
      } else if (this.brandingKey == CustomClients.Standard) {
        statusField.options = this.assetCommonService.getStandardAssetFilterStatusList(this.canPipe, this.AnyAsset);
      } else {
        statusField.options = this.assetCommonService.getAssetFilterStatusList(this.canPipe, this.AnyAsset);
      }
    }
    if (this.env.brandingKey === CustomClients.Vorwerk) this.collectionEventsService.collectionFilterDataSubject$.next(this.filterCollectionFormData);
  }

  /**
   * Update form data on filter dropdown change
   * @param event
   */
  updateFormData(event: AnyType) {
    this.assetsPageService.updateFormData(event.data, this.filterCollectionFormData, event.assetSidebar);
  }

  /**
   * Rest custom form field (channel and type)
   */
  resetCustomFields(fields: string[]) {
    // show or hide channel and type based on the patch value
    fields.forEach((item) => {
      this.assetCommonService.findFormFields(item, false, this.filterCollectionFormData);
    });
  }

  resetForm() {
    this.appliedFilters = 0;
    this.filteredList = this.assetsDetailList;
    this.selectedFilterObject = {};
    this.collectionEventsService.collectionFilterBubblesSubject$.next(this.filterBubbles);
    this.assetCommonService.selectedFilterDataSubject$.next({ selectedFilterObject: this.selectedFilterObject });
    this.collectionEventsService.collectionAssetDataSubject$.next(this.filteredList);
    this.collectionEventsService.splitCollectionAssets.next(this.currentSize);
  }

  /**
   * Utility method to show/hide link based on role
   */
  private _handleRoleSpecificRoutes() {
    if (!this.canPipe.transform(this.AnyAsset, 'collection.history')) {
      this.navLinks = this.navLinks.filter((navLink) => navLink.path !== 'collection-history');
    }
  }

  viewAssetTags() {
    const thumbnailListComponent = this.outlet?.component;
    if (thumbnailListComponent && thumbnailListComponent instanceof ThumbnailListComponent) {
      thumbnailListComponent.viewAssetTags();
    }
  }

  ngOnDestroy(): void {
    this.removeAssetSub?.unsubscribe();
    this.collectionSub?.unsubscribe();
    this.collectionEventsService.collectionAssetDataSubject$.next([]);
    this.collectionEventsService.collectionApplyFilterSubject$.next({});
    // I'm commenting this out because it is resetting the selected items on every collection asset view destroy which is not needed
    // and also causing issues when we navigate back to the collection from asset details view as the selected items are lost
    // To be deleted later after testing; refer clickup ticket https://app.clickup.com/t/86c5e441u
    // this.collectionSelectionService.setSelectedItems('collection', [], false);
    this.exportAssetInfoSub?.unsubscribe();
    this.removeFilterBubblesSubs?.unsubscribe();
    this.removeAllFilterBubblesSubs?.unsubscribe();
    this.filterActionsSubs?.unsubscribe();
    this.formDataSubs?.unsubscribe();
    this.updateCollectionSub?.unsubscribe();
    this.reloadCounterSubs?.unsubscribe();
  }

  toggleMobileSearch() {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    if (this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.remove('mb-8');
    }
    if (!this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.add('mb-8');
    }
  }

  onEvent(event: AnyType) {
    this.isMobileSearchOpen = false;
    document.querySelector('.actions-container')?.classList.add('mb-8');
  }
}
